import axios, { AxiosError, AxiosResponse } from 'axios'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { GenericQueryOptions } from '.'
import { DetailedUser, EditUser, UserGrid, UserPagedQuery, UserProperties } from '../dtos'
import { UserRole } from '../dtos/User/UserRole'

const baseUrl = process.env.PUBLIC_URL
const apiUrl = process.env.REACT_APP_USER_API
const url = `${baseUrl}/${apiUrl}`

export const useSearchUsers = () => {
  return useMutation((search: UserPagedQuery) => axios.post<UserGrid>(`${url}/Search`, search), {})
}

export const useGetCurrentUser = () => {
  return useQuery<AxiosResponse<DetailedUser>, AxiosError>(
    ['user', 'current'],
    () => axios.get<DetailedUser>(`${url}/GetCurrent`),
    {},
  )
}

export const useGetUser = (userId: string) => {
  return useQuery<AxiosResponse<DetailedUser>, AxiosError>(
    ['user', userId],
    () => axios.get<DetailedUser>(`${url}/GetById?userId=${userId}`),
    {},
  )
}

export const useGetUserProperties = (
  userId: string,
  options?: Omit<UseQueryOptions<AxiosResponse<UserProperties>>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery(
    ['user', userId, 'properties'],
    () => axios.get<UserProperties>(`${url}/GetUserProperties?userId=${userId}`),
    options,
  )
}

export const useGetRoles = (userId: string | null) => {
  return useQuery(
    ['user', userId, 'roles'],
    () =>
      axios.get<{ role: UserRole; roleName: string }[]>(`${url}/GetRoles`, { params: { userId } }),
    {},
  )
}

export const useGetUserTypes = () => {
  return useQuery(
    ['userTypes'],
    () => axios.get<{ id: number; itemName: string }[]>(`${url}/GetUserTypes`),
    {},
  )
}

export const useUpdateUser = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation(['user', userId], (user: EditUser) => axios.post(`${url}/UpdateUser`, user), {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['user', variables.userId])
    },
  })
}

export const useCheckUserNameExists = (
  userName: string,
  options?: GenericQueryOptions<boolean>,
) => {
  return useQuery(
    ['checkUserNameExists', userName],
    () => axios.get<boolean>(`${url}/CheckUserNameExists`, { params: { userName } }),
    options,
  )
}

export const exportUrl = `${url}/Export`
